/**
 * Public FAQ — /faq
 *
 * Cyannect-wide FAQ for both shoppers and merchants. Merchants who need
 * the deeper billing/entitlement/integration details are pointed at
 * `/merchant/faq` from the "For merchants — go deeper" panel below.
 *
 * SEO-public (whitelisted in AccessGate.js + backend access.py + sitemap).
 * Uses native <details>/<summary> for a11y and mobile responsiveness.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, ShoppingBag, Store, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageBackground from "@/components/PageBackground";
import { usePricing } from "@/lib/pricing";
import { planPriceLines } from "@/pages/merchant/faqPricing";

const SHOPPER_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting started",
    faqs: [
      {
        q: "What is Cyannect?",
        a: "Cyannect is Canada's performance commerce marketplace — a place where verified brands publish coupon and cashback offers, and shoppers save on every purchase. Every offer is either an Automatic Discount (applied at the brand's checkout) or a Coupon Code (you copy, paste at checkout).",
      },
      {
        q: "Is Cyannect free for shoppers?",
        a: "Yes. Browsing offers, saving favourites, redeeming coupons and earning cashback are all free. Cyannect earns a performance fee from the merchant only when a sale is verified.",
      },
      {
        q: "Do I need an account?",
        a: "You can browse offers without an account. You'll need to sign up to save coupons, favourite brands, enable push notifications and collect cashback.",
      },
    ],
  },
  {
    id: "coupons-and-offers",
    title: "Coupons & offers",
    faqs: [
      {
        q: "What's the difference between Automatic Discount and Coupon Code?",
        a: [
          "Automatic Discount — you click the offer, we open the merchant's checkout with a tracking token and the discount is applied automatically. No code to copy.",
          "Coupon Code — you copy the code we display and paste it at the merchant's checkout. Same discount, one extra click.",
          "The merchant chooses which method to publish; both are equally valid on Cyannect.",
        ],
      },
      {
        q: "Are Cyannect coupons verified?",
        a: "Yes. Every offer is created by a verified merchant account and reviewed before it goes live. If a code stops working, tap the report button on the offer and we'll investigate within one business day.",
      },
      {
        q: "Why is an offer only available in some provinces?",
        a: "Merchants choose which Canadian provinces to activate an offer in based on their shipping, licensing or promotional strategy. If your province isn't listed on an offer, the merchant hasn't enabled it there — try their storefront page for other offers targeting your region.",
      },
    ],
  },
  {
    id: "cashback-and-wallet",
    title: "Cashback & wallet",
    faqs: [
      {
        q: "How does cashback work?",
        a: "When you shop through an eligible Cyannect offer, we track the sale via the merchant's integration. After the merchant confirms the sale is final (typically 15 business days to allow for refunds/returns), your cashback is unlocked and added to your Cyannect wallet.",
      },
      {
        q: "When can I withdraw my cashback?",
        a: "Cashback becomes withdrawable after the merchant's finality window (usually 15 business days after the sale). Withdrawal thresholds and payout methods are shown in your wallet.",
      },
      {
        q: "Why is my cashback showing as pending?",
        a: "Pending means the merchant has reported the sale but the finality window hasn't elapsed yet. This protects both you and the merchant from refunds/chargebacks affecting your balance.",
      },
    ],
  },
  {
    id: "notifications-privacy",
    title: "Notifications & privacy",
    faqs: [
      {
        q: "How do push notifications work?",
        a: "If you favourite a brand and enable push, Cyannect notifies you within seconds when that brand publishes a new offer. Rate-limited to one push per brand per hour so we never spam you.",
      },
      {
        q: "How can I turn notifications off?",
        a: "Open your wallet page, tap the notification card and toggle off. Your device's browser or app settings can also fully revoke permission.",
      },
      {
        q: "What data does Cyannect collect?",
        a: (
          <>
            The short version: your email, sign-up info, saved coupons, and click/conversion metadata to
            pay you cashback and improve offer relevance. Full details are in our{" "}
            <Link to="/privacy" className="text-cyan-700 underline">Privacy Policy</Link>.
          </>
        ),
      },
    ],
  },
];

const MERCHANT_SECTIONS = [
  {
    id: "merchant-basics",
    title: "For merchants — the basics",
    faqs: [
      {
        q: "What is a Cyannect Storefront?",
        a: "A Storefront is a shopper-facing branded business presence inside Cyannect. It can represent a distinct brand, division, service line, business unit, banner, or independently managed catalogue. Categories live inside a Storefront — a Storefront is not just a product category.",
      },
      {
        id: "pricing",
        q: "How much does Cyannect cost merchants?",
        a: [
          "Starter — CA$39.99/mo (1 storefront in 1 province, 4.25% or CA$1.99 per verified sale).",
          "Growth — CA$79.99/mo (3 storefronts × 3 provinces each, 2.85% or CA$1.49 per verified sale).",
          "Extra province: CA$11.99/mo per storefront · Extra storefront: CA$19.99/mo (bundles up to 3 provinces on either plan) · Annual add-ons receive a flat 15% discount.",
        ],
      },
      {
        q: "How do I integrate Cyannect with my checkout?",
        a: "You integrate once. Then create as many Storefronts and Offers as you like — Cyannect validates each Offer through the same underlying integration. Both Automatic Discount and Coupon Code flows are supported out of the box.",
      },
      {
        q: "Do I need a mobile app to sell on Cyannect?",
        a: "No — a merchant mobile app is optional. If your customers can purchase through your website, Cyannect can send shoppers directly to your existing checkout and track referrals + verified conversions server-to-server. This covers SaaS, retail, fashion, beauty, electronics, home, and any other category with a supported web checkout.",
      },
    ],
  },
];

function flattenSections(sections) {
  const rows = [];
  sections.forEach((sec) => {
    sec.faqs.forEach((f, i) => rows.push({ ...f, section: sec.title, sectionId: sec.id, idx: i }));
  });
  return rows;
}

function matchesQuery(row, q) {
  if (!q) return true;
  const hay = `${row.q} ${row.section} ${Array.isArray(row.a) ? row.a.join(" ") : (typeof row.a === "string" ? row.a : "")}`.toLowerCase();
  return hay.includes(q);
}

export default function FAQ() {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "merchant" ? "merchant" : "shopper");   // shopper | merchant
  const q = query.trim().toLowerCase();

  const pricing = usePricing();
  const merchantSections = useMemo(() => (pricing?.final_active
    ? MERCHANT_SECTIONS.map((sec) => ({ ...sec, faqs: sec.faqs.map((f) => (f.id === "pricing" ? { ...f, a: planPriceLines(pricing) } : f)) }))
    : MERCHANT_SECTIONS), [pricing]);
  const activeSections = tab === "merchant" ? merchantSections : SHOPPER_SECTIONS;
  const flat = useMemo(() => flattenSections(activeSections), [activeSections]);
  const filtered = useMemo(
    () => flat.filter((r) => matchesQuery(r, q)),
    [flat, q],
  );

  return (
    <div className="relative min-h-screen">
      <Seo
        title="Cyannect FAQ · Shoppers, cashback, merchants"
        description="Answers about Cyannect coupons, cashback, verified offers, dual redemption (Automatic Discount vs Coupon Code), provinces, and merchant plans. Canada's performance commerce marketplace."
        path="/faq"
        keywords="cyannect faq, cashback canada, coupon codes, verified coupons, merchant faq, merchant plan pricing"
      />
      <PageBackground variant="mint-cyan" />

      <div className="relative max-w-4xl mx-auto px-6 py-16" data-testid="public-faq-page">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-3 py-1 text-xs font-medium text-slate-700 border border-slate-200 mb-4">
            <HelpCircle className="w-3.5 h-3.5 text-[#4A0D18]" /> Cyannect FAQ
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Everything you'd ask about Cyannect
          </h1>
          <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">
            Shoppers below · Merchants further down. If you can't find your
            question here, email us at <a href="mailto:info@cyannect.com" className="text-cyan-700 underline">info@cyannect.com</a>.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6" data-testid="public-faq-tabs">
          <div className="inline-flex rounded-full bg-white/70 backdrop-blur border border-slate-200 p-1 shadow-sm">
            <button
              onClick={() => setTab("shopper")}
              data-testid="public-faq-tab-shopper"
              className={
                "px-5 py-1.5 text-sm font-semibold rounded-full inline-flex items-center gap-2 transition " +
                (tab === "shopper"
                  ? "bg-[#4A0D18] text-white shadow"
                  : "text-slate-700 hover:text-slate-900")
              }
            >
              <ShoppingBag className="w-4 h-4" /> Shoppers
            </button>
            <button
              onClick={() => setTab("merchant")}
              data-testid="public-faq-tab-merchant"
              className={
                "px-5 py-1.5 text-sm font-semibold rounded-full inline-flex items-center gap-2 transition " +
                (tab === "merchant"
                  ? "bg-[#4A0D18] text-white shadow"
                  : "text-slate-700 hover:text-slate-900")
              }
            >
              <Store className="w-4 h-4" /> Merchants
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === "merchant" ? "Search merchant FAQs…" : "Search shopper FAQs…"}
            data-testid="public-faq-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-300 bg-white/80 backdrop-blur text-sm focus:ring-2 focus:ring-[#4A0D18] focus:outline-none"
          />
          {q && (
            <div
              className="text-xs text-slate-500 mt-2 ml-4"
              data-testid="public-faq-search-count"
            >
              {filtered.length} match{filtered.length === 1 ? "" : "es"} for "{query}"
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {activeSections.map((sec) => {
            const visible = sec.faqs.filter((f) => matchesQuery({ ...f, section: sec.title }, q));
            if (q && visible.length === 0) return null;
            return (
              <section key={sec.id} id={sec.id} data-testid={`public-faq-section-${sec.id}`}>
                <h2 className="text-lg font-display font-semibold text-slate-900 mb-3">{sec.title}</h2>
                <div className="pf-card divide-y divide-slate-100 overflow-hidden">
                  {visible.map((f, i) => (
                    <details
                      key={i}
                      open={!!q}
                      className="group px-5 py-4 open:bg-cream-50/40"
                      data-testid={`public-faq-item-${sec.id}-${i}`}
                    >
                      <summary className="flex items-center justify-between cursor-pointer list-none text-sm font-semibold text-slate-900">
                        <span>{f.q}</span>
                        <span className="ml-3 shrink-0 w-6 h-6 rounded-full grid place-items-center bg-slate-100 group-open:bg-[#4A0D18] group-open:text-white text-slate-600 text-lg leading-none transition">
                          <span className="group-open:hidden">+</span>
                          <span className="hidden group-open:inline">×</span>
                        </span>
                      </summary>
                      <div className="mt-3 text-sm text-slate-700 leading-relaxed space-y-2">
                        {Array.isArray(f.a)
                          ? f.a.map((line, li) =>
                              line === "" ? <br key={li} /> : <p key={li}>{line}</p>
                            )
                          : typeof f.a === "string"
                          ? <p>{f.a}</p>
                          : f.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Merchant cross-links: merchant tab only */}
        {tab !== "shopper" && (
          <div className="mt-10 space-y-4">
            <div className="pf-card p-5 flex items-start gap-4" data-testid="public-faq-crosslink">
              <Store className="w-6 h-6 text-[#4A0D18] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Are you a merchant?</div>
                <p className="text-sm text-slate-600 mt-1">
                  For Storefronts, per-province billing, add-on math, upgrades,
                  Stripe finality windows and integration details, see the deeper
                  merchant FAQ.
                </p>
                <Link
                  to="/merchant/faq"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#4A0D18] hover:text-[#2E0B12]"
                  data-testid="public-faq-to-merchant-faq"
                >
                  Merchant FAQ <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="pf-card p-5 flex items-start gap-4" data-testid="public-faq-register-card">
              <ShoppingBag className="w-6 h-6 text-cyan-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Ready to list your brand?</div>
                <p className="text-sm text-slate-600 mt-1">
                  Sign up in under a minute, then publish your first Storefront
                  and offer. Verified merchants can accept Automatic Discount +
                  Coupon Code offers on day one.
                </p>
                <Link
                  to="/register?role=merchant"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-cyan-700 hover:text-cyan-800"
                  data-testid="public-faq-to-register"
                >
                  Get started <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
